/**
 * ============================================================================
 * PayoutDetailModal - DETAILED VIEW OF SINGLE PAYOUT
 * ============================================================================
 *
 * ⚠️  CRITICAL WARNINGS FOR FUTURE CLAUDE SESSIONS:
 *
 * 1. SHOWS COMPLETE PAYOUT INFORMATION
 *    - Transfer details
 *    - Financial breakdown
 *    - Included bookings list
 *    - Status timeline
 *
 * 2. ESCAPE TO CLOSE
 *    - Press Escape key to close modal
 *    - Click outside to close
 *    - X button to close
 *
 * 3. BOOKING LIST
 *    - Shows all bookings included in payout
 *    - Customer names, court names, dates
 *    - Individual booking amounts
 *
 * ============================================================================
 */

import React, { useEffect } from 'react';
import { X, CheckCircle, Clock, XCircle, AlertCircle, CreditCard, Calendar, DollarSign, MessageCircle } from 'lucide-react';
import { formatCurrency, formatDate, getPayoutStatusColor } from '../../utils/revenueFormatters';
import type { PayoutHistory } from '../../types/revenue';

interface PayoutDetailModalProps {
  payout: PayoutHistory | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Payout detail modal showing complete payout information
 */
export function PayoutDetailModal({ payout, isOpen, onClose }: PayoutDetailModalProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !payout) return null;

  const contactAdminForRetry = () => {
    const message = `Hello,

My payout has failed and needs to be retried:

Payout Reference: ${payout.transfer_reference}
Transfer Code: ${payout.transfer_code || 'N/A'}
Amount: ${formatCurrency(payout.net_payout)}
Failure Reason: ${payout.failure_reason || 'Unknown'}
Bookings: ${payout.booking_references || 'N/A'}

Please help me resolve this issue.

Thank you!`;

    const whatsappNumber = process.env.REACT_APP_ADMIN_WHATSAPP || '2349160244454';
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const getStatusIcon = () => {
    switch (payout.status.toLowerCase()) {
      case 'completed':
        return <CheckCircle className="w-8 h-8 text-green-600" />;
      case 'processing':
        return <Clock className="w-8 h-8 text-blue-600" />;
      case 'pending':
        return <AlertCircle className="w-8 h-8 text-yellow-600" />;
      case 'failed':
        return <XCircle className="w-8 h-8 text-red-600" />;
      default:
        return <AlertCircle className="w-8 h-8 text-gray-600" />;
    }
  };

  const statusColorClass = getPayoutStatusColor(payout.status);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div
        className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {getStatusIcon()}
            <div>
              <h2 className="text-xl font-black text-gray-900">Payout Details</h2>
              <p className="text-sm text-gray-600">{payout.transfer_reference}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status */}
          <div className="flex items-center justify-center">
            <span className={`px-4 py-2 rounded-xl text-sm font-bold ${statusColorClass}`}>
              {payout.status}
            </span>
          </div>

          {/* Amount Card */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
            <p className="text-sm text-gray-600 mb-2">Net Payout Amount</p>
            <p className="text-4xl font-black text-gray-900">{formatCurrency(payout.net_payout)}</p>
          </div>

          {/* Financial Breakdown */}
          {payout.gross_revenue && (
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <h3 className="font-bold text-gray-900 mb-3">Financial Breakdown</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Gross Revenue:</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(payout.gross_revenue)}</span>
                </div>
                {payout.platform_commission && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Platform Commission:</span>
                    <span className="font-semibold text-red-600">
                      -{formatCurrency(payout.platform_commission)}
                    </span>
                  </div>
                )}
                {payout.refunds_deducted && payout.refunds_deducted > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Refunds Deducted:</span>
                    <span className="font-semibold text-red-600">
                      -{formatCurrency(payout.refunds_deducted)}
                    </span>
                  </div>
                )}
                <div className="pt-2 border-t border-gray-300 flex items-center justify-between">
                  <span className="font-bold text-gray-900">Net Payout:</span>
                  <span className="font-black text-green-600">{formatCurrency(payout.net_payout)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Transfer Information */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Calendar className="w-4 h-4 text-blue-600" />
                <p className="text-sm font-semibold text-gray-600">Created</p>
              </div>
              <p className="font-bold text-gray-900">{formatDate(payout.created_at)}</p>
            </div>

            {payout.completed_at && (
              <div className="bg-green-50 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <p className="text-sm font-semibold text-gray-600">Completed</p>
                </div>
                <p className="font-bold text-gray-900">{formatDate(payout.completed_at)}</p>
              </div>
            )}

            <div className="bg-purple-50 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <CreditCard className="w-4 h-4 text-purple-600" />
                <p className="text-sm font-semibold text-gray-600">Reference</p>
              </div>
              <p className="font-mono font-bold text-gray-900 text-sm">{payout.transfer_reference}</p>
            </div>

            {payout.transfer_code && (
              <div className="bg-orange-50 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <DollarSign className="w-4 h-4 text-orange-600" />
                  <p className="text-sm font-semibold text-gray-600">Transfer Code</p>
                </div>
                <p className="font-mono font-bold text-gray-900 text-sm">{payout.transfer_code}</p>
              </div>
            )}
          </div>

          {/* Period */}
          {payout.period_start && payout.period_end && (
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm font-semibold text-gray-600 mb-2">Payout Period</p>
              <p className="font-bold text-gray-900">
                {formatDate(payout.period_start)} → {formatDate(payout.period_end)}
              </p>
            </div>
          )}

          {/* Failure Reason */}
          {payout.failure_reason && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-red-600 mb-2">Failure Reason</p>
              <p className="text-gray-900">{payout.failure_reason}</p>
            </div>
          )}

          {/* Included Bookings */}
          <div>
            <h3 className="font-bold text-gray-900 mb-3">
              Included Bookings ({payout.total_bookings || payout.bookings?.length || 0})
            </h3>

            {payout.bookings && payout.bookings.length > 0 ? (
              <div className="border border-gray-200 rounded-xl divide-y divide-gray-200 max-h-64 overflow-y-auto">
                {payout.bookings.map((booking) => (
                  <div key={booking.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-gray-900">{booking.customer_name}</p>
                        <p className="text-sm text-gray-600">{booking.court_name}</p>
                        <p className="text-xs text-gray-500">
                          {booking.booking_date} • {booking.booking_reference}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900">{formatCurrency(booking.venue_share)}</p>
                        {booking.type && (
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            booking.type === 'single' ? 'bg-blue-100 text-blue-700' :
                            booking.type === 'recurring' ? 'bg-green-100 text-green-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {booking.type === 'single' ? '🎾 Single' :
                             booking.type === 'recurring' ? '🔁 Recurring' :
                             '💪 Membership'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : payout.booking_references ? (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-700 mb-2">
                  <strong>ℹ️ Bookings Information</strong>
                </p>
                <p className="text-sm text-gray-700 mb-2">
                  These bookings are linked to this {payout.status} payout:
                </p>
                <p className="font-mono text-sm text-gray-900 break-words">
                  {payout.booking_references}
                </p>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <p className="text-sm text-gray-600">No booking information available</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 space-y-3">
          {/* Contact Admin Button for Failed Payouts */}
          {payout.status.toLowerCase() === 'failed' && (
            <button
              onClick={contactAdminForRetry}
              className="w-full px-4 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
            >
              <MessageCircle className="w-5 h-5" />
              <span>Contact Admin for Retry (WhatsApp)</span>
            </button>
          )}

          {/* Processing Payout Info */}
          {payout.status.toLowerCase() === 'processing' && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-sm text-blue-700 mb-1">
                <strong>⏳ Waiting for OTP Approval</strong>
              </p>
              <p className="text-xs text-gray-600">
                Please check your Paystack dashboard to approve the transfer. OTP expires in 30 minutes.
              </p>
            </div>
          )}

          <button
            onClick={onClose}
            className="w-full px-4 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
